import {
  acceptedCurrencies,
  groupCurrencies,
  type CurrencyNetworkOption,
} from '@features/projects/lib/currency-selection';
import type { CurrencyDto } from '@shared/api/contracts';

export type ReconciliationFilterOption = { value: string; label: string };

const normalizeCode = (value: string) => value.trim().toUpperCase();

/** Код сети для query сверки: TRON вместо TRC20 и т.п. */
export const reconciliationNetworkFilterValue = (option: CurrencyNetworkOption): string => {
  const code = normalizeCode(option.code);
  const chainName = normalizeCode(option.fullName);
  if (/^(TRC20|ERC20|BEP20)$/.test(code) && chainName) return chainName;
  return code;
};

export const buildReconciliationAssetNetworkOptions = (currencies: CurrencyDto[]) => {
  const groups = groupCurrencies(acceptedCurrencies(currencies));

  const assetOptions: ReconciliationFilterOption[] = groups.map((group) => ({
    value: group.code,
    label: group.code,
  }));

  const networkByCode = new Map<string, string>();

  groups.forEach((group) => {
    group.options.forEach((option) => {
      const value = reconciliationNetworkFilterValue(option);
      if (!value) return;
      const label = option.label || option.fullName || option.code;
      if (!networkByCode.has(value)) networkByCode.set(value, label);
    });
  });

  const allNetworkOptions: ReconciliationFilterOption[] = [...networkByCode.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, label]) => ({ value, label }));

  const networkOptionsForAsset = (assetCode?: string): ReconciliationFilterOption[] => {
    if (!assetCode) return allNetworkOptions;
    const group = groups.find((item) => item.code === normalizeCode(assetCode));
    if (!group) return allNetworkOptions;

    const seen = new Set<string>();
    const options: ReconciliationFilterOption[] = [];
    group.options.forEach((option) => {
      const value = reconciliationNetworkFilterValue(option);
      if (!value || seen.has(value)) return;
      seen.add(value);
      options.push({
        value,
        label: option.label || option.fullName || option.code,
      });
    });
    return options.sort((a, b) => a.value.localeCompare(b.value));
  };

  return { assetOptions, allNetworkOptions, networkOptionsForAsset };
};

export const reconcileNetworkAfterAssetChange = (
  assetCode: string | undefined,
  networkCode: string | undefined,
  networkOptionsForAsset: (asset?: string) => ReconciliationFilterOption[],
): string | undefined => {
  if (!networkCode) return undefined;
  const allowed = networkOptionsForAsset(assetCode).some((item) => item.value === normalizeCode(networkCode));
  return allowed ? networkCode : undefined;
};
